<template>
  <div class="header-destinations-dropdown w-full shadow-lg">
    <div class="header-destinations-dropdown__container">
      <NuxtLink
        v-for="destination in destinationsWithUrls"
        :key="destination.id"
        class="header-destinations-dropdown__item group"
        :to="destination.link"
        @click="onClickDestination(destination.titleEn)"
      >
        <div class="header-destinations-dropdown__image-wrapper">
          <FeaturedThumbnailImage :alt="destination.titleEn" :src="destination.imageUrl" />
          <!-- Title overlay on image -->
          <p
            class="header-destinations-dropdown__title absolute inset-0 flex items-center justify-center text-center font-Spectral text-xl font-medium capitalize text-white"
            style="letter-spacing: 0.5px; line-height: 28px"
          >
            {{ destination.titleEn }}
          </p>
        </div>
      </NuxtLink>
    </div>
    <div class="header-destinations-dropdown__footer">
      <button class="header-destinations-dropdown__view-more" type="button" @click="onClickViewMore">
        <span>View more</span>
        <span class="header-destinations-dropdown__view-more-arrow" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import FeaturedThumbnailImage from '@/wabunka-src/common/features/Featured/components/FeaturedThumbnailImage/FeaturedThumbnailImage.vue';
import { getBackgroundImageUrl, type MinimumFeaturedListViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { sendGAWabunkaClickHeaderNavEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';

interface Props {
  destinations: MinimumFeaturedListViewModel;
}

const props = defineProps<Props>();

const { open } = useGlobalModal('searchExperiencesModal');
const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');

const destinationsWithUrls = computed(() =>
  props.destinations.map((destination) => ({
    ...destination,
    link: `/${destination.id}/`,
    imageUrl: getBackgroundImageUrl(destination),
  })),
);

const onClickDestination = (titleEn: string) => {
  sendGAWabunkaClickHeaderNavEvent('destinations', titleEn, 'header');
};

const onClickViewMore = () => {
  sendGAWabunkaClickHeaderNavEvent('destinations', 'view_more', 'header');
  updateTabIndex(0);
  open();
};
</script>

<style lang="scss" scoped>
.header-destinations-dropdown {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  &__container {
    display: flex;
    justify-content: center;
    gap: 4px;
    max-width: 1400px;
    margin: 0 auto;
    padding: 32px 48px 16px;
  }
  &__item {
    flex: 1 1 0;
    max-width: 200px;
    min-width: 0;
  }
  &__image-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
  }
  &__item:hover &__title {
    text-decoration: underline;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 48px 24px;
  }
  &__view-more {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #171717;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-destinations-dropdown__view-more-arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more-arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
}
</style>
